<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { refDebounced } from '@vueuse/core'
import { keepPreviousData } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import useAuthStore, { isUserToken } from '@/stores/authStore'
import { useInfiniteQuery, useQueries, useQuery } from '@/composables/api'
import { sanitizeRegex } from '@/utils/textConversions'
import { useAuth } from '@/composables/useAuth'
import { useUserSettings } from '@/composables/useUserSettings'
import type { components } from '@/types/_api'
import BSelect from '@/components/base/BSelect/BSelect.vue'
import mdiPencil from '~icons/mdi/pencil'

type Creditor = components['schemas']['Creditor']
type CreditorTag = components['schemas']['Creditor-Tag']

const { t } = useI18n()
const authStore = useAuthStore()
const { isSystemUser } = useAuth()
const { settings: userSettings, updateSetting } = useUserSettings()

const creditorSelection = computed(() => userSettings.value.dashboard?.creditor_selection)

const allSelectedForCheckbox = computed(() => {
  const selection = creditorSelection.value
  return selection?.all_selected === true && (selection.excluded == null || selection.excluded.length === 0)
})

const excludedIds = computed(() => {
  const selection = creditorSelection.value
  if (selection?.all_selected !== true) return undefined
  return (selection.excluded ?? []).map((item: { creditor?: string }) => item.creditor)
})

const selectedCreditors = computed(() => {
  const selection = creditorSelection.value
  if (selection == null) return []

  if (selection.all_selected) {
    const excluded = new Set((selection.excluded ?? []).map((item: { creditor?: string }) => item.creditor))
    return creditors.value.filter((c) => !excluded.has(c._id)).map((c) => c._id)
  }
  return (selection.included ?? []).map((item: { creditor?: string }) => item.creditor)
})

const creditorSearch = ref<string | null>('')
const debouncedSearch = refDebounced(creditorSearch, 300)

const tagsQuery = useInfiniteQuery(
  '/creditor--tags',
  computed(() => ({
    query: { sort: 'name', projection: { name: 1, _id: 1 } },
  })),
  { placeholderData: keepPreviousData }
)
const allTags = computed<CreditorTag[]>(() => {
  if (tagsQuery.data.value?.pages == null) return []
  return tagsQuery.data.value.pages.flatMap((page) => page._items ?? [])
})
const tagFilterChips = computed(() => allTags.value.map((tag) => ({ value: tag._id, text: tag.name })))

function onTagsLoadMore() {
  if (tagsQuery.hasNextPage.value && !tagsQuery.isFetchingNextPage.value) {
    void tagsQuery.fetchNextPage()
  }
}

const selectedTagIds = computed<string[]>({
  get: () => (userSettings.value.dashboard?.creditor_tag_selection ?? []).map((item) => item.creditor_tag),
  set: (ids) => {
    updateSetting(
      'dashboard.creditor_tag_selection',
      ids.map((id) => ({ creditor_tag: id }))
    )
    updateSetting('dashboard.creditor_selection', {
      all_selected: true,
      included: [],
      excluded: [],
    })
  },
})

const creditorWhereFilter = computed(() => {
  const where: Record<string, unknown> = { is_active: true }
  const trimmed = debouncedSearch.value?.trim() ?? ''
  if (trimmed !== '') {
    where.name = { $regex: sanitizeRegex(trimmed), $options: 'i' }
  }
  if (selectedTagIds.value.length > 0) {
    where['creditor_tags.creditor_tag'] = { $in: selectedTagIds.value }
  }
  return where
})

const creditorsQuery = useInfiniteQuery(
  '/creditors',
  computed(() => ({
    query: {
      where: creditorWhereFilter.value,
      sort: 'name',
      projection: { name: 1, _id: 1 },
    },
  })),
  { placeholderData: keepPreviousData }
)

const creditors = computed<Creditor[]>(() => {
  if (creditorsQuery.data.value?.pages == null) return []
  return creditorsQuery.data.value.pages.flatMap((page) => page._items ?? [])
})

// Separate query for the unfiltered total. `showFilter` gates the entire
// section on `totalCreditors > 1`, so we can't derive the value from the
// in-dropdown-filtered `creditorsQuery` — if the user reloads with a narrow
// tag selection active, the filtered total would be < 2 and the filter would
// hide itself, with no way to clear it. Cheap query: only `_meta.total`.
const totalCreditorsQuery = useQuery(
  '/creditors',
  computed(() => ({
    query: {
      where: { is_active: true },
      projection: { _id: 1 },
      max_results: 1,
    },
  }))
)
const totalCreditors = computed(() => totalCreditorsQuery.data.value?._meta?.total ?? 0)

// Powers the "+N matching" chip text when a tag filter is active. Intentionally
// excludes `debouncedSearch` so the chip stays stable while the user types.
const tagFilteredTotalQuery = useQuery(
  '/creditors',
  computed(() => ({
    query: {
      where: {
        is_active: true,
        ...(selectedTagIds.value.length > 0 ? { 'creditor_tags.creditor_tag': { $in: selectedTagIds.value } } : {}),
      },
      projection: { _id: 1 },
      max_results: 1,
    },
  }))
)
const tagFilteredTotal = computed(() => tagFilteredTotalQuery.data.value?._meta?.total ?? totalCreditors.value)

function onLoadMore() {
  if (creditorsQuery.hasNextPage.value && !creditorsQuery.isFetchingNextPage.value) {
    void creditorsQuery.fetchNextPage()
  }
}

function onSelectAll(selected: boolean) {
  if (!selected) {
    updateSetting('dashboard.creditor_selection', {
      all_selected: false,
      included: [],
      excluded: [],
    })
    return
  }

  updateSetting('dashboard.creditor_selection', {
    all_selected: true,
    included: [],
    excluded: [],
  })
}

const userId = computed(() => {
  return isUserToken(authStore.selectedToken?.payload) ? authStore.selectedToken.payload.u_id : null
})

const menuOpen = ref(false)
const initializedForUser = ref<string | null>(null)

const notificationCountQueries = computed<
  ['/creditor--notifications', { query: Record<string, unknown> }, { enabled: boolean }][]
>(() =>
  creditors.value.map((creditor) => [
    '/creditor--notifications',
    {
      query: {
        where: {
          creditor: creditor._id,
          hidden_by_creditor: { $ne: true },
          hidden_by_system: { $ne: true },
          is_read: { $ne: true },
        },
        projection: { _id: 1 },
        max_results: 1,
      },
    },
    { enabled: menuOpen.value },
  ])
)

// @ts-expect-error useQueries wrapper types issue
const notificationCountsResult = useQueries(notificationCountQueries)

const notificationCountsByCreditor = computed(() => {
  const results = notificationCountsResult.value as { data?: { _meta?: { total?: number } } }[]
  return new Map(
    creditors.value
      .map((creditor, idx) => [creditor._id, results[idx]?.data?._meta?.total ?? 0] as const)
      .filter(([, count]) => count > 0)
  )
})

const creditorsWithBadge = computed(() =>
  creditors.value.map((creditor) => ({
    ...creditor,
    notificationCount: notificationCountsByCreditor.value.get(creditor._id) ?? 0,
  }))
)

watch(
  [creditors, userId],
  ([newCreditors, currentUserId]) => {
    if (newCreditors.length === 0 || currentUserId == null) {
      return
    }

    if (initializedForUser.value === currentUserId) {
      return
    }

    initializedForUser.value = currentUserId

    if (creditorSelection.value == null) {
      updateSetting('dashboard.creditor_selection', {
        all_selected: !isSystemUser.value,
        included: [],
        excluded: [],
      })
    }
  },
  { immediate: true }
)

const selectedCreditorText = computed(() => {
  const selection = creditorSelection.value
  const excludedCount = selection?.excluded?.length ?? 0
  const includedCount = selection?.included?.length ?? 0
  const hasTagFilter = selectedTagIds.value.length > 0

  // "Alla" only when truly all active creditors are effectively selected.
  // With an active tag filter the selection is narrowed even if no creditors
  // are explicitly excluded, so we fall through to the "+N" path instead.
  if (selection?.all_selected && excludedCount === 0 && !hasTagFilter) {
    return t('common.all')
  }

  const actualCount = selection?.all_selected ? tagFilteredTotal.value - excludedCount : includedCount

  if (actualCount === 0) return '-'

  const firstCreditorName = creditors.value.find((c) => c._id === selectedCreditors.value[0])?.name ?? '-'
  if (actualCount === 1) return firstCreditorName

  return `${firstCreditorName}, +${actualCount - 1}`
})

const showFilter = computed(() => totalCreditors.value > 1)

function onSelectionChange(value: unknown) {
  const newSelection = value as string[]
  const newSelectionSet = new Set(newSelection)
  const currentSelection = creditorSelection.value

  if (currentSelection?.all_selected === true) {
    const newExcludedFromLoaded = creditors.value.filter((c) => !newSelectionSet.has(c._id)).map((c) => c._id)

    const excludedIdsSet = new Set(
      (currentSelection.excluded ?? []).map((item: { creditor?: string }) => item.creditor)
    )

    for (const id of newExcludedFromLoaded) {
      excludedIdsSet.add(id)
    }
    for (const id of newSelection) {
      excludedIdsSet.delete(id)
    }

    const excluded = Array.from(excludedIdsSet).map((id) => ({ creditor: id }))

    if (excluded.length >= totalCreditors.value) {
      updateSetting('dashboard.creditor_selection', {
        all_selected: false,
        included: [],
        excluded: [],
      })
    } else {
      updateSetting('dashboard.creditor_selection', {
        all_selected: true,
        included: [],
        excluded,
      })
    }
    return
  }

  const selectedCount = newSelection.length
  const totalCount = totalCreditors.value

  if (selectedCount >= totalCount / 2 && selectedCount > 0) {
    const excluded = creditors.value.filter((c) => !newSelectionSet.has(c._id)).map((c) => ({ creditor: c._id }))

    updateSetting('dashboard.creditor_selection', {
      all_selected: true,
      included: [],
      excluded,
    })
  } else {
    const included = newSelection.map((id) => ({ creditor: id }))

    updateSetting('dashboard.creditor_selection', {
      all_selected: false,
      included,
      excluded: [],
    })
  }
}
</script>

<template>
  <section v-if="showFilter">
    <v-chip
      variant="outlined"
      size="small"
      label
      @click="menuOpen = !menuOpen"
    >
      {{ t('common.creditor') }}: {{ selectedCreditorText }}
      <v-icon
        end
        size="small"
        :icon="mdiPencil"
      />
    </v-chip>

    <b-select
      v-model:menu="menuOpen"
      v-model:search="creditorSearch"
      v-model:selected-filter-chips="selectedTagIds"
      :model-value="selectedCreditors"
      :items="creditorsWithBadge"
      :filter-chips="tagFilterChips"
      :filter-chips-max-rows="1"
      :filter-chips-has-more="tagsQuery.hasNextPage.value"
      :filter-chips-loading="tagsQuery.isFetchingNextPage.value"
      :total-items="totalCreditors"
      :all-selected="allSelectedForCheckbox"
      :excluded-ids="excludedIds"
      :has-more="creditorsQuery.hasNextPage.value"
      :loading="creditorsQuery.isFetchingNextPage.value"
      item-title="name"
      item-value="_id"
      item-badge="notificationCount"
      min-width="min(90vw, 400px)"
      multiple
      no-filter
      hide-details
      hide-input
      custom-select-all
      @update:model-value="onSelectionChange"
      @select-all="onSelectAll"
      @load-more="onLoadMore"
      @filter-chips-load-more="onTagsLoadMore"
    />
  </section>
</template>
